import { useEffect, useState, type ReactNode } from 'react';
import Popover from '@mui/material/Popover';
import { useAppSelector } from '../../app/state';
import { callAction } from '../../app/actions';
import { ItemRow } from '../../components/ItemRow';
import { DEFAULT_COLOR } from '../../lib/color';
import * as Me from '../../api/me';

/**
 * The bell's panel (plan Phase 3): what changed, in two sections.
 *
 * "Subscribed" is this user's notifications — changes to things they watch, each BOLD UNTIL
 * READ. "All changes" is everything they are allowed to see from the last 30 days, one row
 * per document (the server dedupes to the latest revision), grouped by day. Click a row and
 * the document opens; nothing here is a new navigation mechanism — rows go through
 * `openDocInCurrentMode`, the same call the "My items" pane makes, so deep-link URL sync
 * comes for free.
 *
 * THE FEED PAYLOAD IS PANEL-LOCAL, the search dropdown's rule (plan 2.2): only the unread
 * COUNT is global state (the badge), so fetching the panel's contents on open costs nobody
 * else a render. The two watermark moves are actions, because they change the badge:
 * OPENING posts the 'changes' scope (the All-changes count clears; notifications stay
 * unread), "Mark all read" posts 'all'.
 *
 * `locallyRead` exists because the rows on screen come from the fetch that PRECEDED any
 * mark-read: it records what the person read while the panel was open, so a clicked row
 * unbolds without refetching and without pretending the server answered.
 *
 * VISIBILITY IS NOT CHECKED HERE, unlike "My items" — deliberately. The feed endpoint
 * filters against `getAllowedDocIds()` at read time on the server (the security boundary);
 * a second client-side pass could only disagree with it.
 *
 * NOTIFICATIONS CARRY KINDS since V0.38: a `revision` row renders as it always has (its
 * human/AI flavour moved from `kind` to `changeKind`); the two assignment kinds render as
 * "You were assigned …" rows with the assigner's name when it still resolves. An assign_node
 * row navigates like a node row (centre + popup); an assign_doc row opens the page.
 *
 * A NAME IS DELIMITED ONCE (V0.45.2). A row names a thing inside a sentence, and something has
 * to mark where that name ends. A NODE is marked by its own colour and carries no quotes; a
 * DOCUMENT has no colour in this app, so it keeps the quotes it always had. A row that is
 * nothing BUT the name — a revision row's bare title — needs neither, and never had them.
 * Quotes are therefore not decoration here: they are the fallback delimiter for a name with no
 * colour, which is also why an unresolvable node falls back to them (see `nodeName`).
 */

const kindIcon = (kind: 'human' | 'ai') => (kind === 'ai' ? '#i-ai' : '#i-edit');

/** Today / Yesterday / a local date — the panel's day headers. */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString();
}

/** "just now" / "12 min ago" / "2 h ago" / "3 d ago" — the row's trailing time. */
function relTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

export function FeedPanel({
  open,
  anchorEl,
  onClose,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  const locale = useAppSelector((s) => s.currentLocale);
  // The map, for the node colours below. The container is replaced on every publish, so this
  // re-renders the panel with the app — which `FeedBell`'s own `useAppState()` already does,
  // so it costs nothing new.
  const nodes = useAppSelector((s) => s.nodes);
  const [feed, setFeed] = useState<Me.FeedPayload | null>(null);
  const [failed, setFailed] = useState(false);
  const [locallyRead, setLocallyRead] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFeed(null);
    setFailed(false);
    setLocallyRead(new Set());
    Me.fetchFeed(locale)
      .then((f) => {
        if (cancelled) return;
        setFeed(f);
        // Opening IS seeing the All-changes section. What the badge still owes after this
        // is the individually-unread notifications, and this fetch just said how many.
        callAction('markFeedSeen', 'changes', f.unread.subscribed);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, locale]);

  const isUnread = (n: Me.FeedNotification) => !n.readAt && !locallyRead.has(n.id);

  /**
   * A node's name as this panel prints it: the node's OWN COLOUR, no quote marks.
   *
   * THE RAW STORED COLOUR, in both themes, at the user's explicit direction. Measured against
   * the nine colours in the map, raw is comfortable on the dark panel (5.0–7.2:1) and thin on
   * the white one — #2bb0d8, #e0982a and #1fb6a8 land at 2.4–2.5:1 against #ffffff, where 13px
   * text wants 4.5. `NodeChip` handles the same problem by darkening 35% for light-theme text
   * (`shade(c, -0.35)`, which brings all nine to 5.2–7.0), and that was offered and declined in
   * favour of the true colour. Recorded so nobody 'fixes' it as an oversight: it is a decision,
   * and `shade()` is one call away if it is ever revisited.
   *
   * NO COLOUR TO READ means the quotes come back rather than a guess being made. That happens
   * when the node is not in this client's map — pre-boot, or a branch V0.43 withholds — and it
   * keeps the name delimited by SOMETHING in every case. A node that IS present but has no
   * stored colour takes `DEFAULT_COLOR`, the app's answer everywhere else.
   */
  const nodeName = (nodeId: string | null, label: string): ReactNode => {
    const node = nodeId ? nodes[nodeId] : undefined;
    if (!node) return `“${label}”`;
    return (
      <span className="feed-nodename" style={{ color: node.color || DEFAULT_COLOR }}>
        {label}
      </span>
    );
  };

  const markRead = (notif: Me.FeedNotification) => {
    if (isUnread(notif)) {
      setLocallyRead(new Set([...locallyRead, notif.id]));
      callAction('markNotificationRead', notif.id);
    }
  };

  const openRow = (row: Me.FeedChange, notif?: Me.FeedNotification) => {
    if (notif) markRead(notif);
    onClose();
    callAction(
      'openDocInCurrentMode',
      row.docPageId,
      (row.nodeLabel ? row.nodeLabel + ' — ' : '') + row.title,
      row.nodeId,
    );
  };

  const markAll = () => {
    if (!feed) return;
    setLocallyRead(new Set(feed.notifications.map((n) => n.id)));
    callAction('markFeedSeen', 'all', 0);
  };

  const loadMore = () => {
    if (!feed) return;
    Me.fetchFeed(locale, feed.page + 1)
      .then((f2) =>
        setFeed((cur) =>
          cur
            ? { ...cur, changes: [...cur.changes, ...f2.changes], page: f2.page, hasMore: f2.hasMore }
            : cur,
        ),
      )
      .catch(() => {});
  };

  const row = (r: Me.FeedChange, notif?: Me.FeedNotification) => (
    <ItemRow
      key={notif ? notif.id : r.revisionId}
      className={notif && isUnread(notif) ? 'feedrow unread' : 'feedrow'}
      data={notif ? { 'data-feed-notif': notif.id } : { 'data-feed-change': r.revisionId }}
      icon={
        <svg className="ico">
          <use href={kindIcon(r.kind)} />
        </svg>
      }
      label={r.title}
      title={`${r.title} — ${r.kind === 'ai' ? 'AI' : 'edited'} · ${r.actorLabel} · ${new Date(r.createdAt).toLocaleString()}`}
      onClick={() => openRow(r, notif)}
      trailing={
        <span className="feed-meta">
          {r.actorLabel} · {relTime(r.createdAt)}
        </span>
      }
    />
  );

  /** One notification, whatever its kind. A revision row reuses `row()` above verbatim. */
  const notifRow = (n: Me.FeedNotification) => {
    if (n.kind === 'revision') {
      return row(
        {
          revisionId: n.revisionId ?? n.id,
          docPageId: n.docPageId ?? '',
          nodeId: n.nodeId,
          createdAt: n.createdAt,
          title: n.title ?? 'Untitled',
          nodeLabel: n.nodeLabel,
          actorLabel: n.actorLabel ?? '',
          // The human/AI flavour moved to changeKind (V0.38); a row without one reads human.
          kind: n.changeKind ?? 'human',
        },
        n,
      );
    }

    // A deleted document (V0.41): no click target — the page is gone. The title rides the row
    // as `label` (or the resolved `title` fallback), because it can no longer be looked up.
    if (n.kind === 'doc_deleted') {
      const gone = n.label ?? n.title ?? 'A document';
      const label = `“${gone}” was deleted${n.actorLabel ? ` by ${n.actorLabel}` : ''}`;
      return (
        <ItemRow
          key={n.id}
          className={isUnread(n) ? 'feedrow unread' : 'feedrow'}
          data={{ 'data-feed-notif': n.id }}
          icon={
            <svg className="ico">
              <use href="#i-trash" />
            </svg>
          }
          label={label}
          title={`${label} · ${new Date(n.createdAt).toLocaleString()}`}
          onClick={() => markRead(n)}
          trailing={
            <span className="feed-meta">
              {n.actorLabel ? `${n.actorLabel} · ` : ''}
              {relTime(n.createdAt)}
            </span>
          }
        />
      );
    }

    // A stale address (V0.45.1). Its whole reason for being in the bell: renaming closes the
    // popup, so the "!" on the re-slug button is behind a panel nobody is looking at by the
    // time it appears. `label` is the stale slug — the fact that stops being lookupable the
    // moment somebody changes the address again.
    if (n.kind === 'slug_stale') {
      const what = n.nodeLabel ?? n.title ?? 'Something';
      // The name is resolved LIVE, like every other kind's — and the row is pruned the moment a
      // rename gives the thing a different base, so the name printed here is always the one the
      // standing row was raised about. See docs/slugNotifications.ts for why that is the fix
      // rather than storing the name.
      //
      // This kind is raised about a node OR a document, and each is delimited its own way (see
      // the header): the node by its colour, the document by the quotes.
      const named = n.nodeId ? nodeName(n.nodeId, what) : `“${what}”`;
      const label = <>{named}’s URL mismatches its title.</>;
      const open = () => {
        markRead(n);
        onClose();
        // Straight to the thing, with its name bar — which is where the button is.
        if (n.docPageId) {
          callAction(
            'openDocInCurrentMode',
            n.docPageId,
            (n.nodeLabel ? n.nodeLabel + ' — ' : '') + (n.title ?? 'Documentation'),
            n.nodeId,
          );
        } else if (n.nodeId) {
          callAction('focusPopup', n.nodeId);
        }
      };
      return (
        <ItemRow
          key={n.id}
          className={isUnread(n) ? 'feedrow unread' : 'feedrow'}
          data={{ 'data-feed-notif': n.id }}
          icon={
            <svg className="ico">
              <use href="#i-reslug" />
            </svg>
          }
          label={label}
          // The stale address itself, which the sentence above no longer prints: on hover,
          // because it is the detail somebody deciding what to do about this actually wants.
          title={`Still at ${n.label ?? 'its old address'} · ${new Date(n.createdAt).toLocaleString()}`}
          onClick={open}
          trailing={
            <span className="feed-meta">
              {n.actorLabel ? `${n.actorLabel} · ` : ''}
              {relTime(n.createdAt)}
            </span>
          }
        />
      );
    }

    // TO-DOS (V0.50). Two kinds, one shape, and they are in the bell for the reason the
    // assignment kinds are: a to-do handed to you is WORK, not news, so it reaches you whether
    // or not you subscribed to anything.
    //
    // The TITLE RIDES THE ROW (`label`) rather than being resolved, like 'doc_deleted' above
    // and unlike every other kind — a task's title lives in exactly one place and the row can
    // outlive it, so a resolved title would leave a blank line for every task since completed
    // and cleared. Both navigate to the node, which is where the list is.
    if (n.kind === 'todo_assigned' || n.kind === 'todo_done') {
      const what = n.label ?? 'a task';
      const where = n.nodeLabel ?? 'a node';
      const assigned = n.kind === 'todo_assigned';
      const label = assigned ? (
        <>
          You were given “{what}” on {nodeName(n.nodeId, where)}
        </>
      ) : (
        <>
          “{what}” is done on {nodeName(n.nodeId, where)}
        </>
      );
      const labelText = assigned
        ? `You were given “${what}” on ${where}`
        : `“${what}” is done on ${where}`;
      return (
        <ItemRow
          key={n.id}
          className={isUnread(n) ? 'feedrow unread' : 'feedrow'}
          data={{ 'data-feed-notif': n.id }}
          icon={
            <svg className="ico">
              <use href={assigned ? '#i-list' : '#i-check'} />
            </svg>
          }
          label={label}
          title={`${labelText} · ${new Date(n.createdAt).toLocaleString()}`}
          onClick={() => {
            markRead(n);
            onClose();
            // The node, not the to-do: a to-do has no surface of its own, and the popup's
            // section is where somebody can actually act on it.
            if (n.nodeId) callAction('focusPopup', n.nodeId);
          }}
          trailing={
            <span className="feed-meta">
              {n.actorLabel ? `${n.actorLabel} · ` : ''}
              {relTime(n.createdAt)}
            </span>
          }
        />
      );
    }

    // The assignment kinds: "You were assigned …". An assign_doc names a DOCUMENT and keeps
    // its quotes (and its locale, since assignments are per language); an assign_node names a
    // NODE, so the colour delimits it instead — see the header.
    const what =
      n.kind === 'assign_doc'
        ? `${n.title ?? 'a document'}${n.locale ? ` (${n.locale.toUpperCase()})` : ''}`
        : (n.nodeLabel ?? 'a node');
    const named = n.kind === 'assign_doc' ? `“${what}”` : nodeName(n.nodeId, what);
    const label = <>You were assigned {named}</>;
    const labelText = `You were assigned “${what}”`;
    const open = () => {
      markRead(n);
      onClose();
      if (n.kind === 'assign_doc' && n.docPageId) {
        callAction(
          'openDocInCurrentMode',
          n.docPageId,
          (n.nodeLabel ? n.nodeLabel + ' — ' : '') + (n.title ?? 'Documentation'),
          n.nodeId,
        );
      } else if (n.nodeId) {
        callAction('focusPopup', n.nodeId);
      }
    };
    return (
      <ItemRow
        key={n.id}
        className={isUnread(n) ? 'feedrow unread' : 'feedrow'}
        data={{ 'data-feed-notif': n.id }}
        icon={
          <svg className="ico">
            <use href="#i-users" />
          </svg>
        }
        label={label}
        title={`${labelText}${n.actorLabel ? ` · by ${n.actorLabel}` : ''} · ${new Date(n.createdAt).toLocaleString()}`}
        onClick={open}
        trailing={
          <span className="feed-meta">
            {n.actorLabel ? `${n.actorLabel} · ` : ''}
            {relTime(n.createdAt)}
          </span>
        }
      />
    );
  };

  /* Day-grouped All-changes rows: a header whenever the day flips (rows arrive newest
     first, so one pass is enough). */
  const changeRows: ReactNode[] = [];
  if (feed) {
    let lastDay = '';
    for (const c of feed.changes) {
      const day = dayLabel(c.createdAt);
      if (day !== lastDay) {
        lastDay = day;
        changeRows.push(
          <div className="feed-day" key={`day-${day}`}>
            {day}
          </div>,
        );
      }
      changeRows.push(row(c));
    }
  }

  const empty = feed && feed.notifications.length === 0 && feed.changes.length === 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      marginThreshold={8}
      // The page never scrolls (body is overflow:hidden) — same reasoning as the "…" menus.
      disableScrollLock
      slotProps={{ paper: { id: 'feed-panel', className: 'feedpanel' } }}
    >
      <div className="feed-head">
        <span className="feed-title">What changed</span>
        {feed && !empty ? (
          <button type="button" className="feed-markread" id="feed-markread" onClick={markAll}>
            Mark all read
          </button>
        ) : null}
      </div>

      {!feed && !failed ? <div className="feed-status">Loading…</div> : null}
      {failed ? <div className="feed-status">Couldn’t load the feed.</div> : null}
      {empty ? (
        <div className="feed-empty" id="feed-empty">
          You’re all caught up.
        </div>
      ) : null}

      {feed && feed.notifications.length ? (
        <>
          <div className="feed-sect">Subscribed</div>
          {feed.notifications.map(notifRow)}
        </>
      ) : null}

      {feed && feed.changes.length ? (
        <>
          <div className="feed-sect">All changes</div>
          {changeRows}
          {feed.hasMore ? (
            <div className="feed-more" id="feed-more" onClick={loadMore}>
              Show older changes
            </div>
          ) : null}
        </>
      ) : null}
    </Popover>
  );
}
